import { apiPaths } from 'configs/apiConfigs'
import { httpClaims } from 'utils/http'

export type EntityDef = {
  slot: string
  typeId: 'string' | 'dateonly' | string
  description: string
  entId: number
  category: number
  trackingEnabled: boolean
}

export type ClaimRow = {
  importedJson?: Record<string, unknown>
  [key: string]: any
}

export type ColumnConfig = { field?: string }

// --- Entities fetch (cached) ---

let entitiesCache: EntityDef[] | null = null
let entitiesInFlight: Promise<EntityDef[]> | null = null

async function fetchEntities(): Promise<EntityDef[]> {
  if (entitiesCache) return entitiesCache
  if (entitiesInFlight) return entitiesInFlight

  entitiesInFlight = (async () => {
    try {
      const response = await httpClaims.get(apiPaths.entities())
      const rawEntities = Array.isArray(response?.data) ? response.data : []

      const mappedEntities: EntityDef[] = rawEntities
        .map((rawEntity: any): EntityDef | null => {
          const entId = Number(rawEntity?.entId)
          const slotName = rawEntity?.slot
          if (!slotName || !Number.isFinite(entId)) return null
          return {
            slot: slotName,
            typeId: rawEntity?.typeId ?? 'string',
            description: rawEntity?.description ?? slotName,
            entId,
            category: Number(rawEntity?.category) || 0,
            trackingEnabled: Boolean(rawEntity?.trackingEnabled),
          }
        })
        .filter(Boolean) as EntityDef[]

      entitiesCache = mappedEntities
      return mappedEntities
    } catch (error) {
      console.error('fetchEntities: failed', error)
      entitiesCache = []
      return []
    } finally {
      entitiesInFlight = null
    }
  })()

  return entitiesInFlight
}

export function clearEntitiesCache() {
  entitiesCache = null
}

const isValidValue = (value: unknown) => !(value === null || value === undefined || value === '')

const normalizeValue = (value: unknown) => (typeof value === 'string' ? value.trim() : value)

const toSnakeCase = (text: string): string =>
  text
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()

// --- Main ---

export async function hydrateRowsFromEntities(
  claimRows: ClaimRow[],
  columnDefs: ColumnConfig[],
  columnKeys?: readonly string[],
): Promise<ClaimRow[]> {
  if (!Array.isArray(claimRows) || claimRows.length === 0) return claimRows
  if (!Array.isArray(columnDefs) || columnDefs.length === 0) return claimRows

  const entities = await fetchEntities()

  const entIdBySlotName = new Map<string, number>()
  for (const entity of entities) entIdBySlotName.set(entity.slot, entity.entId)

  for (const claimRow of claimRows) {
    const importedJson = claimRow.importedJson ?? {}

    for (let colIndex = 0; colIndex < columnDefs.length; colIndex++) {
      const columnField = columnDefs[colIndex]?.field
      if (!columnField) continue
      if (columnField === 'claimStatusPayer' || columnField === 'detailedClaimStatus') {
        if (!isValidValue(claimRow[columnField])) claimRow[columnField] = ''
        continue
      }

      const slotKey = columnKeys?.[colIndex] ?? toSnakeCase(columnField)
      const entId = entIdBySlotName.get(slotKey)

      // 1) importedJson via entId
      if (typeof entId === 'number') {
        const importedValue = importedJson[String(entId)]
        if (isValidValue(importedValue)) {
          claimRow[columnField] = normalizeValue(importedValue)
          continue
        }
      }

      // 2) keep existing if valid
      if (isValidValue(claimRow[columnField])) {
        continue
      }

      // 3) fallback
      claimRow[columnField] = ''
    }
  }

  return claimRows
}
